import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { User } from '../../types';
import { useTheme } from '../../hooks/useTheme';

interface HeaderProps {
    user: User | null;
    onLogout: () => void;
    onLoginClick: () => void;
    onUpgradeClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ user, onLogout, onLoginClick, onUpgradeClick }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { theme, toggleTheme, isDark } = useTheme();
    const [showProfileMenu, setShowProfileMenu] = useState(false);

    const isActive = (path: string) => location.pathname.startsWith(path);

    return (
        <nav className="glass sticky top-0 z-50 border-b border-white/20 dark:border-slate-700/50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between h-16">
                    {/* Logo */}
                    <div
                        className="flex items-center cursor-pointer group"
                        onClick={() => navigate('/')}
                    >
                        <div className="text-3xl mr-3 group-hover:scale-110 transition-transform duration-300">🚀</div>
                        <div>
                            <span className="font-display font-bold text-xl tracking-tight gradient-text">
                                ThrustED
                            </span>
                            <span className="text-[10px] block text-slate-500 dark:text-slate-400 font-medium tracking-wider uppercase">
                                Ace Your Exams
                            </span>
                        </div>
                    </div>

                    {/* Right Section */}
                    <div className="flex items-center space-x-2 sm:space-x-4">
                        {/* Dark Mode Toggle */}
                        <button
                            onClick={toggleTheme}
                            className="w-10 h-10 flex items-center justify-center rounded-xl text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all duration-300 hover:scale-105"
                            title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                        >
                            {isDark ? (
                                <i className="fas fa-sun text-lg text-yellow-400"></i>
                            ) : (
                                <i className="fas fa-moon text-lg"></i>
                            )}
                        </button>

                        {/* Admin Button */}
                        {user?.role === 'admin' && (
                            <button
                                onClick={() => navigate('/admin')}
                                className={`hidden sm:flex items-center font-medium px-4 py-2 rounded-xl transition-all duration-300 ${isActive('/admin')
                                        ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg shadow-orange-500/25'
                                        : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                                    }`}
                            >
                                <i className="fas fa-shield-alt mr-2"></i>Admin
                            </button>
                        )}

                        {/* Dashboard Button */}
                        {user && (
                            <button
                                onClick={() => navigate('/dashboard')}
                                className={`hidden sm:flex items-center font-medium px-4 py-2 rounded-xl transition-all duration-300 ${isActive('/dashboard')
                                        ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg shadow-orange-500/25'
                                        : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                                    }`}
                            >
                                <i className="fas fa-chart-line mr-2"></i>Dashboard
                            </button>
                        )}

                        {/* User Section */}
                        {user ? (
                            <div className="relative">
                                <button
                                    onClick={() => setShowProfileMenu(!showProfileMenu)}
                                    className="flex items-center space-x-3 pl-3 border-l border-slate-200 dark:border-slate-700"
                                >
                                    {/* Avatar */}
                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center text-white font-bold shadow-lg shadow-orange-500/25 hover:scale-105 transition-transform">
                                        {(user.name || user.email)[0].toUpperCase()}
                                    </div>

                                    {/* Name & Badge */}
                                    <div className="hidden sm:block text-left">
                                        <div className="text-sm font-bold text-slate-800 dark:text-white flex items-center">
                                            {user.name || user.email.split('@')[0]}
                                            {user.subscriptionTier !== 'free' && (
                                                <span className="ml-2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white text-[9px] px-2 py-0.5 rounded-full uppercase tracking-wider font-bold animate-pulse-slow">
                                                    {user.subscriptionTier}
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wider">{user.role}</div>
                                    </div>

                                    <i className="fas fa-chevron-down text-xs text-slate-400 hidden sm:block"></i>
                                </button>

                                {/* Dropdown Menu */}
                                {showProfileMenu && (
                                    <>
                                        <div
                                            className="fixed inset-0 z-40"
                                            onClick={() => setShowProfileMenu(false)}
                                        />
                                        <div className="absolute right-0 mt-2 w-56 rounded-2xl glass shadow-glass-lg py-2 z-50 animate-scale-in">
                                            <button
                                                onClick={() => { navigate('/profile'); setShowProfileMenu(false); }}
                                                className="w-full px-4 py-3 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center"
                                            >
                                                <i className="fas fa-user-circle mr-3 text-slate-400"></i>
                                                My Profile
                                            </button>
                                            <button
                                                onClick={() => { navigate('/dashboard'); setShowProfileMenu(false); }}
                                                className="w-full px-4 py-3 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center sm:hidden"
                                            >
                                                <i className="fas fa-chart-line mr-3 text-slate-400"></i>
                                                Dashboard
                                            </button>
                                            {user.subscriptionTier === 'free' && (
                                                <button
                                                    onClick={() => { onUpgradeClick(); setShowProfileMenu(false); }}
                                                    className="w-full px-4 py-3 text-left text-sm text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 flex items-center"
                                                >
                                                    <i className="fas fa-crown mr-3"></i>
                                                    Upgrade to Pro
                                                </button>
                                            )}
                                            <div className="border-t border-slate-200 dark:border-slate-700 my-2"></div>
                                            <button
                                                onClick={() => { onLogout(); setShowProfileMenu(false); }}
                                                className="w-full px-4 py-3 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center"
                                            >
                                                <i className="fas fa-sign-out-alt mr-3"></i>
                                                Logout
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        ) : (
                            <button
                                onClick={onLoginClick}
                                className="btn-primary text-white px-6 py-2.5 rounded-xl font-semibold text-sm"
                            >
                                <i className="fas fa-sign-in-alt mr-2"></i>
                                Login
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </nav>
    );
};

export default Header;
